import { Dispatch } from 'react-redux';
import { AuthActionCreators, IApplicationState } from "../";
import { IBaseResponse, IFollowUpDonorForList, IFollowUpSurvey, IFollowUpSurveyCounts, IFollowUpSurveyForList, IFollowUpSurveyShow, IListItem } from '@nkr/models/index';
import { FollowUpSurveyListType } from '@nkr/enums/index';

export interface IFollowUpSurveyCountsResponse {
    data: {
        success: boolean,
        message: string,
        results: IFollowUpSurveyCounts
    }
}

export interface IFollowUpSurveyListResponse {
    data: {
        success: boolean,
        message: string,
        results: IFollowUpSurveyForList[]
    }
}

export interface IFollowUpSurveyResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            current: IFollowUpSurvey,
            previous: IFollowUpSurvey,
            reviewers: IListItem[]
        }
    }
}

export interface IFollowUpDonorListResponse {
    data: {
        success: boolean,
        message: string,
        results: IFollowUpDonorForList[]
    }
}

export const ActionCreators = {
    getSurveyCounts: (centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IFollowUpSurveyCountsResponse> => {
        let options = {
            url: `${__API__}/follow-up/survey/counts?centerId=${centerId != null ? centerId : ''}`
        };

        try {
            return await dispatch(AuthActionCreators.apiGet(options));
        } catch (error) {
            throw error;
        }
    },
    getSurveyList: (listType: FollowUpSurveyListType, centerId: number, firstName: string, lastName: string, startDate: string, endDate: string, show: IFollowUpSurveyShow) => async (dispatch: Dispatch<IApplicationState>): Promise<IFollowUpSurveyListResponse> => {
        let options = {
            url: `${__API__}/follow-up/surveys`,
            data: {
                listType,
                centerId,
                firstName,
                lastName,
                startDate,
                endDate,
                show
            }
        };

        try {
            return await dispatch(AuthActionCreators.apiPost(options));
        } catch (error) {
            throw error;
        }
    },
    getSurvey: (id: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IFollowUpSurveyResponse> => {
        let options = {
            url: `${__API__}/follow-up/survey/${id}`
        };

        try {
            return await dispatch(AuthActionCreators.apiGet(options));
        } catch (error) {
            throw error;
        }
    },
    getSurveyPdf: (id: number) => async (dispatch: Dispatch<IApplicationState>) => {
        let options = {
            url: `${__API__}/follow-up/survey/${id}/pdf`,
            responseType: 'blob'
        };

        try {
            return await dispatch(AuthActionCreators.apiGet(options));
        } catch (error) {
            throw error;
        }
    },
    markCompleted: (id: number, completedDateTime: string, areLabResultsGood: boolean, isNotifiedOfLabResults: boolean, labResultsReviewedByContactId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/follow-up/survey/${id}/mark-complete`,
            data: {
                completedDateTime,
                areLabResultsGood,
                isNotifiedOfLabResults,
                labResultsReviewedByContactId
            }
        };

        try {
            return await dispatch(AuthActionCreators.apiPost(options));
        } catch (error) {
            throw error;
        }
    },
    markIncomplete: (id: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/follow-up/survey/${id}/mark-incomplete`,
        };

        try {
            return await dispatch(AuthActionCreators.apiPost(options));
        } catch (error) {
            throw error;
        }
    },
    markOrdersSent: (id: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/follow-up/survey/${id}/mark-orders-sent`,
        };

        try {
            return await dispatch(AuthActionCreators.apiPost(options));
        } catch (error) {
            throw error;
        }
    },
    appointmentBooked: (id: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/follow-up/survey/${id}/appointment-booked`,
        };

        try {
            return await dispatch(AuthActionCreators.apiPost(options));
        } catch (error) {
            throw error;
        }
    },
    uploadLabOrders: (id: number, file: File) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let formData = new FormData();
        formData.append('file', file);

        let options = {
            url: `${__API__}/follow-up/survey/${id}/upload-lab-orders`,
            data: formData
        };

        try {
            return await dispatch(AuthActionCreators.apiPostFormData(options));
        } catch (error) {
            throw error;
        }
    },
    sendSurveyEmail: (id: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/follow-up/survey/${id}/send-survey-email`,
        };

        try {
            return await dispatch(AuthActionCreators.apiPost(options));
        } catch (error) {
            throw error;
        }
    },
    markReviewed: (id: number, reviewerContactId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/follow-up/survey/${id}/mark-reviewed?reviewerContactId=${reviewerContactId}`,
        };

        try {
            return await dispatch(AuthActionCreators.apiPost(options));
        } catch (error) {
            throw error;
        }
    },
    getDonors: (centerId: number, firstName: string, lastName: string) => async (dispatch: Dispatch<IApplicationState>): Promise<IFollowUpDonorListResponse> => {
        let options = {
            url: `${__API__}/follow-up/donors?centerId=${centerId != null ? centerId : ''}&firstName=${firstName}&lastName=${lastName}`
        };

        try {
            return await dispatch(AuthActionCreators.apiGet(options));
        } catch (error) {
            throw error;
        }
    },
}